/** Content Admin API helpers used by Ops CMS (same admin key). */

import { getAdminKey, getApiBase, OpsApiError } from "./api";

const ADMIN = "/api/content/admin";

export type ContentPostPayload = {
  slug: string;
  title: string;
  status?: "draft" | "publish" | "scheduled" | "private" | "trash";
  post_type?: "post" | "page" | "community_question";
  body_markdown?: string;
  excerpt?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  locale?: string;
  travel_stage?: string | null;
  city?: string | null;
  topic_key?: string | null;
  geo_payload?: Record<string, unknown> | null;
  category_slugs?: string[];
  source?: "manual" | "hermes";
};

async function contentFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const key = getAdminKey();
  if (!key) throw new OpsApiError("Not signed in", 401, "OPS_UNAUTHORIZED");
  const headers = new Headers(init.headers);
  headers.set("X-ChinaPie-Content-Admin-Key", key);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${getApiBase()}${path}`, { ...init, headers });
  if (!res.ok) {
    let code: string | undefined;
    let message = res.statusText;
    let issues: Array<{ field: string; message: string }> | undefined;
    try {
      const body = (await res.json()) as {
        detail?: { error?: { code?: string; message?: string; issues?: Array<{ field: string; message: string }> } };
      };
      code = body.detail?.error?.code;
      message = body.detail?.error?.message || code || message;
      issues = body.detail?.error?.issues;
    } catch {
      /* ignore */
    }
    const err = new OpsApiError(message || "Request failed", res.status, code);
    (err as OpsApiError & { issues?: typeof issues }).issues = issues;
    throw err;
  }
  return (await res.json()) as T;
}

export async function getPostBySlug(slug: string) {
  return contentFetch<Record<string, unknown>>(
    `${ADMIN}/posts/by-slug/${encodeURIComponent(slug.trim().toLowerCase())}`,
  );
}

export async function validatePost(payload: ContentPostPayload) {
  return contentFetch<{ ok: boolean; errors?: Array<{ field: string; message: string }> }>(
    `${ADMIN}/posts/validate`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export async function upsertPostBySlug(slug: string, payload: ContentPostPayload) {
  return contentFetch<Record<string, unknown>>(
    `${ADMIN}/posts/by-slug/${encodeURIComponent(slug.trim().toLowerCase())}`,
    { method: "PUT", body: JSON.stringify({ ...payload, slug }) },
  );
}

export async function trashPost(postId: string) {
  return contentFetch<{ data?: { id: string; status: string } }>(
    `${ADMIN}/posts/${encodeURIComponent(postId)}`,
    { method: "DELETE" },
  );
}
